/* Copyright (c) 2020-present CloudIO Inc */

import { atom, useEvent, useStateXAtomValue } from '@cloudio-saas/statex';
import Controller from 'controller/Controller';
import { usePlatform } from 'platform/PlatformHooks';
import { useCallback } from 'react';
import { globalCache } from 'utils/cache';
import type { CallResult, ControllerInterface } from './ControllerInterface';

function cache(): Record<string, ControllerInterface> {
  return globalCache.get<Record<string, ControllerInterface>>(
    '_controllers_',
    () => ({}),
  ).value;
}

const registerController = (
  appUid: string,
  pageId: string,
  controller: ControllerInterface,
) => {
  cache()[`${appUid}:${pageId}`] = controller;
};

const getController = (appUid: string, pageId: string) =>
  cache()[`${appUid}:${pageId}`];

const controllerFunctionsAtom = atom<string[]>({
  path: ['transient', 'controllerFunctions', ':appUid', ':pageId'],
  defaultValue: [],
});

function useControllerFunctions(appUid: string, pageId: string) {
  return useStateXAtomValue(controllerFunctionsAtom, {
    params: { appUid, pageId },
  });
}

function useSyncControllerFunction<T>({
  functionName,
  itemId,
  pageId,
  appUid,
}: {
  appUid: string;
  pageId: string;
  itemId: string;
  functionName?: string;
}): (props: Record<string, unknown>) => CallResult<T> {
  const platform = usePlatform();
  return useCallback(
    (props: Record<string, unknown>) => {
      if (functionName) {
        return Controller.callSync<T>({
          functionName,
          appUid,
          itemId,
          pageId,
          platform,
          props,
        });
      }
      return { status: 'NONE' };
    },
    [appUid, functionName, itemId, pageId, platform],
  );
}

function useAsyncControllerFunction<T>({
  functionName,
  itemId,
  pageId,
  appUid,
}: {
  appUid: string;
  pageId: string;
  itemId: string;
  functionName?: string;
}): (props: Record<string, unknown>) => Promise<CallResult<T>> {
  const platform = usePlatform();
  return useEvent(async (props: Record<string, unknown>) => {
    if (functionName) {
      return Controller.callAsync<T>({
        functionName,
        appUid,
        itemId,
        pageId,
        platform,
        props,
      });
    }
    return { status: 'NONE' };
  });
}

export {
  controllerFunctionsAtom,
  getController,
  registerController,
  useAsyncControllerFunction,
  useControllerFunctions,
  useSyncControllerFunction,
};
